import Head from 'next/head';
import { GetStaticPaths, GetStaticProps } from 'next/types';
import Header from '@/components/Header';
import PostContent from '@/components/PostContent';
import { container, Registry } from '@/infra/container-registry';
import { BlogPost } from '@/domain/entities/post';
import { BlogAuthor } from '@/domain/entities/author';
import { GetPostUseCase } from '@/application/post/get-post.use-case';
import { GetAuthorUseCase } from '@/application/author/get-author.use-case';
import { ListPostsUseCase } from '@/application/post/list-posts.use-case';
import CategoriesSection from '@/components/CategoriesSection';
import SimilarPosts from '@/components/SimilarPosts';
import ReactHtmlParser from 'html-react-parser';

type PostProps = {
  post: BlogPost;
  author: BlogAuthor;
  featuredPosts: Array<BlogPost>;
};

export default function Single({ post, author, featuredPosts }: PostProps) {
  const parsedDescription = ReactHtmlParser(post.description) as JSX.Element[];
  const { props } = parsedDescription[0];
  const description = props?.children ?? '';
  return (
    <>
      <Head>
        <title>{post.title}</title>
        <meta name="description" content={description} />
        <meta name="twitter:title" content="Kenzie Academy Brasil: Curso de Programação Online" />
        <meta
          name="twitter:description"
          content="Torne-se um desenvolvedor em 12 meses com o curso de programação online da Kenzie Academy Brasil e comece a pagar após estar empregado. Confira!"
        />
        <meta property="og:title" content="Kenzie Academy Brasil: Curso de Programação Online" />
        <link rel="canonical" href={`https://kenzie.com.br/blog/${post.slug}`} />
      </Head>
      <Header />
      <main>
        <PostContent post={post} author={author} />
        <SimilarPosts posts={featuredPosts} />
        <CategoriesSection />
      </main>
    </>
  );
}

export const getStaticPaths: GetStaticPaths = async () => {
  const listPostsUseCase = container.get<ListPostsUseCase>(Registry.ListPostsUseCase);
  const posts = await listPostsUseCase.execute({ per_page: 100 });
  const paths = posts.map((post: BlogPost) => ({
    params: { slug: post.slug },
  }));

  return {
    paths,
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps = async context => {
  const getPostUseCase = container.get<GetPostUseCase>(Registry.GetPostUseCase);
  const post = await getPostUseCase.execute(context.params?.slug as string);

  const getAuthorUseCase = container.get<GetAuthorUseCase>(Registry.GetAuthorUseCase);
  const authorData = await getAuthorUseCase.execute(post.authorId);

  const listPostsUseCase = container.get<ListPostsUseCase>(Registry.ListPostsUseCase);
  const featuredPosts = await listPostsUseCase.execute({
    categories: post.category,
    exclude: post.id,
    per_page: 3,
  });

  return {
    props: {
      post: post,
      author: authorData.toJSON(),
      featuredPosts: featuredPosts,
    },
  };
};
